import * as React from "react";
import { useStore } from "_core/store";
import { Navigate, useNavigate } from "react-router";
import {
  A2A3FormValues,
  AndersenA2A3ChargerCustomisation as AndersenA2A3ChargerCustomisationValidator,
} from "_core/validators/ChargerCustomisation/Andersen";
import { ProductUrls } from "_core/types";
import Mixpanel from "_core/analytics/Mixpanel";
import {
  AnalyticsEvents,
  DataLayerEvent,
} from "_core/analytics/AnalyticsEvents";
import { DataLayer } from "_core/analytics/DataLayer";
import { FormApi, Formy } from "_core/form";
import {
  Button,
  Heading4,
  P,
  RadioCard,
  RadioField,
  Stack,
  Strong,
} from "@ovotech/element";
import { ANDERSEN_A2_COLOURS } from "_pages/ChargerCustomisation/Andersen/constants";
import { AndersenFAQ } from "_pages/ChargerCustomisation/Andersen/AndersenFAQ";
import MidnightBlack_MidnightBlack from "_assets/andersen/ANDERSEN-A2-MIDNIGHTBLACK-MIDNIGHTBLACK.png";
import CheltenhamGraphite_CheltenhamGraphite from "_assets/andersen/ANDERSEN-A2-CHELTENHAMGRAPHITE-CHELTENHAMGRAPHITE.png";
import CotswoldOak_CornishCream from "_assets/andersen/ANDERSEN-A2-COTSWOLDOAK-CORNISHCREAM.png";
import { getPriceString } from "_core/utils/format";
import { SplitHeading } from "_core/components";
import { PhoneContact } from "_core/components/PhoneContact";
import { EmailContact } from "_core/components/EmailContact";

const baseColourImages = {
  MidnightBlack_MidnightBlack,
  CheltenhamGraphite_CheltenhamGraphite,
  CotswoldOak_CornishCream,
};

export const AndersenA2ChargerCustomisation = () => {
  const {
    cableSetup,
    cableLength,
    baseColour,
    setBaseColour,
    offStreetParking,
    charger,
  } = useStore();
  const navigate = useNavigate();
  const handleSubmit = async (values: A2A3FormValues) => {
    if (!values.baseColour) {
      return;
    }
    setBaseColour(values.baseColour);
    navigate(ProductUrls.PersonalDetails);

    Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
      buttonId: "chargerCustomisation-submit-button",
      pageName: ProductUrls.ChargerCustomisation,
      baseColour: values.baseColour,
      charger,
    });

    DataLayer.push({
      event: DataLayerEvent.SubmitChargerCustomisation,
      pageName: ProductUrls.ChargerCustomisation,
      baseColour: values.baseColour,
      charger,
    });
  };

  if (!offStreetParking) return <Navigate to={ProductUrls.Postcode} />;

  return (
    <>
      <Formy
        initialValues={{
          cableLength,
          baseColour,
          cableSetup,
        }}
        validationSchema={AndersenA2A3ChargerCustomisationValidator}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          values,
          errors,
          touched,
          setFieldValue,
        }: FormApi<A2A3FormValues>) => {
          return (
            <form
              data-testid="chargerCustomisation-form"
              onSubmit={handleSubmit}
            >
              <Stack spaceBetween={10}>
                <Stack spaceBetween={8}>
                  <SplitHeading part1="Customise" part2="your charger" />
                  <div>
                    <P size={"lg"}>
                      Personalise your <Strong>Andersen A2</Strong> by choosing
                      the charger colour.
                    </P>
                    <P size={"sm"} className={"SurfaceVariant"}>
                      Need help choosing? Call us on <PhoneContact />, or email
                      us at <EmailContact />.
                    </P>
                  </div>
                </Stack>
                <div>
                  <RadioField
                    className={"RadioField"}
                    id="baseColour"
                    name="baseColour"
                    label={
                      <Heading4 className={"SurfaceHeading"}>
                        Choose a colour
                      </Heading4>
                    }
                    error={touched.baseColour && errors.baseColour}
                  >
                    {ANDERSEN_A2_COLOURS.map((chargerColor) => (
                      <RadioCard
                        className={"RadioCard"}
                        key={chargerColor.value}
                        name={"baseColour"}
                        value={chargerColor.value}
                        id={chargerColor.value}
                        label={
                          <div>
                            <img
                              className={"ChargerImg"}
                              alt={`image of a ${chargerColor.label} charger`}
                              src={baseColourImages[chargerColor.value]}
                            ></img>
                            <P>
                              {chargerColor.label}
                              <Strong>
                                {" "}
                                from {getPriceString(chargerColor.price)}¹
                              </Strong>
                            </P>
                          </div>
                        }
                        checked={values.baseColour === chargerColor.value}
                        onClick={() => {
                          setFieldValue("baseColour", chargerColor.value);
                          Mixpanel.track(AnalyticsEvents.CLICKEDBUTTON, {
                            buttonId: `baseColour-${chargerColor.value}`,
                            pageName: ProductUrls.ChargerCustomisation,
                            charger,
                            value: chargerColor.value,
                          });
                        }}
                      />
                    ))}
                  </RadioField>
                  <P className={"SurfaceVariant"}>
                    ¹ Including standard installation. The final cost will be in
                    the quote we send after we’ve checked your installation
                    survey. All prices above include VAT and are applicable to
                    all colour variants.
                  </P>
                </div>
                <div className={"ButtonContainer"}>
                  <Button
                    variant="outline"
                    iconLeft="chevron-left"
                    data-testid="chargerCustomisation-back-button"
                    onClick={() => navigate(ProductUrls.OffStreetParking)}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    iconRight="chevron-right"
                    data-testid="chargerCustomisation-submit-button"
                  >
                    Next
                  </Button>
                </div>
              </Stack>
            </form>
          );
        }}
      </Formy>
      <AndersenFAQ />
    </>
  );
};
